import { LogIn, Loader2, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { isInTenantSupportMode } from '@/auth/roles'
import { useEnterTenant } from '@/hooks/useEnterTenant'
import { Button } from '@/components/ui/button'
import type { ButtonProps } from '@/components/ui/button'
import { FormError } from '@/components/ui/form-error'

interface LoginToTenantButtonProps {
  tenantId: string
  /** Longer label for page headers; compact for list rows. */
  size?: 'default' | 'sm'
  /** Visual weight — list rows stay outline; page headers use primary. */
  variant?: ButtonProps['variant']
  className?: string
  /**
   * When true, show the full “Log in to tenant” label. When false (list), use
   * the shorter “Log in”.
   */
  fullLabel?: boolean
}

/**
 * Super-admin control to open a tenant in support mode (org-admin powers inside
 * that organization). If the session is already in this tenant, jumps to the
 * tenant home instead of re-entering.
 */
export function LoginToTenantButton({
  tenantId,
  size = 'default',
  variant = 'default',
  className,
  fullLabel = false,
}: LoginToTenantButtonProps) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { enter, enteringId, isEntering, error } = useEnterTenant()
  const busy = enteringId === tenantId
  const alreadyHere = isInTenantSupportMode(user) && user.tenantId === tenantId
  const iconClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'

  if (alreadyHere) {
    return (
      <Button
        type="button"
        size={size}
        variant={variant === 'default' ? 'outline' : variant}
        className={className}
        onClick={() => navigate('/events')}
      >
        <ArrowRight className={iconClass} />
        {fullLabel ? 'Open tenant' : 'Open'}
      </Button>
    )
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        size={size}
        variant={variant}
        className={className}
        disabled={isEntering}
        onClick={() => void enter(tenantId)}
      >
        {busy ? <Loader2 className={`${iconClass} animate-spin`} /> : <LogIn className={iconClass} />}
        {busy ? 'Logging in…' : fullLabel ? 'Log in to tenant' : 'Log in'}
      </Button>
      {error ? <FormError size="sm" className="max-w-[14rem] justify-end text-right">{error}</FormError> : null}
    </div>
  )
}
